<template>
  <Card
    class="integration-card"
    :thumbnail="thumbnail.type === 'image' ? thumbnail.src : undefined"
    thumbnail-size="contain"
  >
    <template v-if="thumbnail.type === 'icon'" #header>
      <Icon :icon="thumbnail.src" size="3x" />
    </template>
    <template #default>
      <component
        :is="value.source.link ? 'a' : 'div'"
        v-bind="{ href: value.source.link }"
        target="_blank"
        class="card__title"
      >
        {{ value.source.name }}
        <Badge v-if="value.source.beta" theme="warning" class="integration-card__beta ml-1" value="Beta" />
        <FontAwesomeIcon v-if="value.source.link" :icon="['fal', 'external-link']" class="ml-1" />
      </component>
      <ReadMore class="integration-card__description" :value="value.source.description" placement="center" :limit="3" />
      <Price
        v-if="value.source.subscription && referenceLoaded"
        class="mt-2"
        style="flex-grow: 0"
        :value="value.source.subscription"
        :options="priceOptions"
        @assisted="$ev => (assisted = $ev)"
      />
    </template>
    <template #footer>
      <template v-if="!value.id">
        <ButtonComponent
          :promise="promise"
          :label="$t('install')"
          :icon="['fal', 'plus']"
          :disabled="Boolean(assisted)"
          class="grow-1"
          @click="$emit('install')"
        />
      </template>
      <template v-else>
        <component
          :is="getComponent(value)"
          :icon="getIcon(value)"
          :theme="getTheme(value)"
          :href="$path('installer.url', value)"
          tag="a"
          class="integration-card__installer"
        >
          <span v-html="getLabel(value)" :slot="getSlot(value)"></span>
        </component>

        <Actions
          placement="integration"
          :query="{ integration: value.id }"
          :use-portal="false"
          label=""
          :icon="['fal', 'ellipsis-v']"
        >
          <template #bottom>
            <ButtonComponent
              :disabled="Boolean(getDisabledField('settings'))"
              v-tooltip.bottom="getDisabledField('settings')"
              v-if="value.source.schema.length"
              :label="$tc('setting', 2)"
              :icon="['fal', 'cog']"
              @click="$emit('update')"
            />
            <ButtonComponent
              :disabled="Boolean(getDisabledField('multiple'))"
              v-tooltip.bottom="getDisabledField('multiple')"
              v-if="value.source.multiple"
              :label="$tc('installMore', 2)"
              :icon="['fal', 'plus']"
              @click="$emit('multiinstall')"
            />
            <ButtonComponent
              :disabled="Boolean(getDisabledField('uninstall'))"
              v-tooltip.bottom="getDisabledField('uninstall')"
              :label="$t('uninstall')"
              confirm
              :promise="promises.uninstall"
              :icon="['fal', 'trash']"
              @onSuccess="() => $emit('uninstalled')"
              @click="uninstall"
            />
          </template>
        </Actions>
      </template>
    </template>
  </Card>
</template>

<script>
import API from '@/api/Integration'
import { path } from 'ramda'
import Card from '@/components/Card'
import Price from '@/components/Pricing/Price'
import Badge from '@/components/Badge'
import Actions from '@/components/Actions'
import ReadMore from '@kvass/read-more'
import { DropdownComponent as Dropdown } from 'vue-elder-dropdown'

export default {
  props: {
    value: Object,
    promise: Promise,
  },
  data() {
    return {
      promises: {
        uninstall: null,
      },
      assisted: null,
    }
  },
  computed: {
    thumbnail() {
      return {
        type: this.value.source.logo.match(/^[(https?:\/\\)\/]/) ? 'image' : 'icon',
        src: this.value.source.logo,
      }
    },
    referenceLoaded() {
      if (!this.referenceState) return true
      return !this.referenceState.states.fetchOne
    },
    referenceState() {
      if (this.$route.name.startsWith('project')) return this.$path('$store.state.Project')
    },
    priceOptions() {
      if (this.$route.name.startsWith('project')) return { units: this.referenceState.item.metadata.limits.units }
    },
  },
  methods: {
    uninstall() {
      this.promises.uninstall = API.delete({ id: this.value.id })
    },

    getDisabledField(field) {
      return path(['metadata', 'disabledFields', field], this.value)
    },

    getComponent(value) {
      if (value.status === 'pending' && this.$path('installer.url', value)) return 'ButtonComponent'
      return 'Badge'
    },
    getIcon(value) {
      if (['active', 'inactive'].includes(value.status)) return ['fal', 'check']
      if (this.$path('installer.url', value)) return ['fal', 'arrow-to-right']
      return ['fal', 'exclamation-triangle']
    },
    getTheme(value) {
      if (value.status === 'active') return 'success'
      return 'warning'
    },

    getLabel(value) {
      if (['active'].includes(value.status)) return this.$t('installed')

      if (['inactive'].includes(value.status)) return this.$t('inactive')

      return this.$path('installer.info', value)
    },

    getSlot(value) {
      if (value.status === 'pending' && this.$path('installer.url', value)) return 'label'

      return 'default'
    },
  },
  components: {
    Card,
    Price,
    Badge,
    Dropdown,
    ReadMore,
    Actions,
  },
}
</script>

<style lang="scss">
.integration-card {
  .card__title {
    text-decoration: none;
    color: inherit;
  }

  &__beta {
    border-radius: var(--border-radius);
    padding: 0.2rem 0.5rem;
    font-size: 0.7em;
  }

  .card__header {
    min-height: 100px;
    padding: var(--card-spacing) calc(var(--card-spacing) * 3);
  }

  .card__content {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
  }

  .card__footer {
    &-content {
      flex-wrap: nowrap;
      gap: 0.5rem;
    }
  }

  .badge {
    justify-content: center;
    font-size: 0.8em;
    flex-grow: 1;
    padding: 0.5em 1em;
    border-radius: var(--border-radius);

    & > span {
      flex-grow: 1;
    }
  }

  &__description {
    white-space: pre-line;
    margin-bottom: auto;
    background-color: white;
  }

  &__installer {
    flex-grow: 1;

    &.elder-button {
      background-color: hsl(var(--warning-h), var(--warning-s), 95%);
      border: 1px solid hsl(var(--warning-h), var(--warning-s), 87%);

      .elder-button__label {
        white-space: normal;
      }
    }
  }
}
</style>
